import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  LeftOutlined,
  LoadingOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { isDesktop, isMobile } from "react-device-detect";
import "./RecomendacaoCollab.scss";
import { useSelector } from "react-redux";
import { Avatar, Badge, Select, Spin, Tag } from "antd";
import ClusterChart from "shared/components/cluster-chart/ClusterChart";
import avatarProdutoresSrc from "shared/assets/images/avatar-produtores.png";
import avatarCompositoresSrc from "shared/assets/images/avatar-compositores.png";
import avatarFeatSrc from "shared/assets/images/avatar-feat.png";
import avatarHitmakerSrc from "shared/assets/images/avatar-hitmaker.png";
import avatarSniperSrc from "shared/assets/images/avatar-sniper.png";
import avatarMassificadoSrc from "shared/assets/images/avatar-massificado.png";
import avatarAscendenciaSrc from "shared/assets/images/avatar-ascendencia.png";
import avatarAtemporalSrc from "shared/assets/images/avatar-atemporal.png";
import { ReactComponent as EllipsisIcon } from "shared/assets/images/icon-ellipsis.svg";
import { ReactComponent as SearchIcon } from "shared/assets/images/icon-search.svg";
import { uniq } from "lodash";
import { getRecommendationByArtistFilter } from "shared/service/StrmService";
import { Trans, useTranslation } from "react-i18next";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";
import useSubscription, {
  SubscriptionPlan,
} from "shared/hooks/useSubscription";
import SubscriptionDefaultCTA from "shared/components/subscription/SubscriptionDefaultCTA";

function importAll(r: any): string[] {
  return r.keys().map(r) as string[];
}

const images = uniq(
  importAll(
    require.context("shared/assets/images/recomendacoes", false, /.png$/)
  )
);

interface Recommendation {
  artistIdSpotify: string;
  artistIdStrm: string | null;
  artistName: string;
  position: number;
  roleTitle: string | null;
  similarityIndex: number;
}

interface Props {
  // Define your component props here
}

enum PageStep {
  Inicio = 0,
  Recomendacao = 1,
}
enum Role {
  Produtor = "producers",
  Compositor = "writers",
  Feat = "performers",
}
enum Caracteristica {
  Hitmaker = "hitmaker",
  Sniper = "sniper",
  Massificado = "masses",
  Ascendência = "trend",
  Atemporal = "trend-max",
}

const genreFilters = [
  { value: "rock" },
  { value: "hiphop" },
  { value: "pop" },
  { value: "funk" },
];

const countryFilters = [
  { value: "BR" },
  { value: "US" },
  { value: "GB" },
  { value: "FR" },
];

const RecomendacaoCollab: React.FC<Props> = () => {
  const { t, i18n } = useTranslation();
  const { currentPlan } = useSubscription();
  const [etapaAtual, setEtapaAtual] = useState<PageStep>(PageStep.Inicio);
  const { userArtist } = useSelector((state: any) => state.artists);
  const { genreOptions } = useSelector((state: any) => state.genres);
  const { countryOptions } = useSelector((state: any) => state.countries);
  const indexRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState<boolean>(false);
  const [showRightArrow, setShowRightArrow] = useState<boolean>(true);
  const [selectedFeature, setSelectedFeature] = useState<Caracteristica>(
    Caracteristica.Hitmaker
  );
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [currentRecommendations, setCurrentRecommendations] = useState<
    Recommendation[]
  >([]);
  const genreFiltersOptions = useMemo(() => {
    return genreOptions.filter((genre: any) => {
      return genreFilters.some((filter) => filter.value === genre.value);
    });
  }, [genreOptions]);

  const countryFiltersOptions = useMemo(() => {
    return countryOptions.filter((country: any) => {
      return countryFilters.some((filter) => filter.value === country.value);
    });
  }, [countryOptions]);
  const [loading, setLoading] = useState<boolean>(false);
  const [indexList, setIndexList] = useState<number>(5);
  const [filter, setFilter] = useState({
    indexes: Caracteristica.Hitmaker,
    role: Role.Produtor,
    genre: "",
    country: "",
  });

  const [selectedGenre, setSelectedGenre] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [showFilter, setShowFilter] = useState<boolean>(false);

  const fullScreenWidth = window.innerWidth;

  const filteredOtherGenres = useMemo(() => {
    return genreOptions.filter((genre: any) => !genre.value?.includes("outro"));
  }, [genreOptions]);

  const randomImages = useMemo(() => {
    return images.sort(() => Math.random() - Math.random()).slice(0, 5);
    // eslint-disable-next-line
  }, [currentRecommendations]);

  const handleNextStep = () => {
    setEtapaAtual(PageStep.Recomendacao);
  };

  const getRecommendations = useCallback(async () => {
    if (!userArtist?.id || currentPlan === SubscriptionPlan.START) return;
    setLoading(true);
    const params = {
      userArtistId: userArtist.id,
      ...filter,
      genre: filter.role === Role.Feat ? filter.genre : "",
    };
    const response: any = await getRecommendationByArtistFilter(params);
    setRecommendations(response);
    setLoading(false);
  }, [filter, userArtist, currentPlan]);

  const onClickRole = (role: Role) => {
    if (currentPlan === SubscriptionPlan.START) return;
    setFilter({ ...filter, role });
  };

  const handleScrollLeft = (ref: HTMLDivElement | null) => {
    if (ref) {
      setShowRightArrow(true);
      setShowLeftArrow(ref.scrollLeft - 100 > 0);
      ref.scrollTo({
        left: ref.scrollLeft - 100,
        behavior: "smooth",
      });
    }
  };

  const handleScrollRight = (ref: HTMLDivElement | null) => {
    if (ref) {
      setShowLeftArrow(true);
      setShowRightArrow(ref.scrollWidth - ref.scrollLeft !== ref.clientWidth);
      ref.scrollTo({
        left: ref.scrollLeft + 100,
        behavior: "smooth",
      });
    }
  };

  const onClickCaracteristica = (caracteristica: Caracteristica) => {
    // setFilter({ ...filter, indexes: caracteristica });
    setSelectedFeature(caracteristica);
  };

  const onClickGenre = (genre: string) => {
    // setFilter({ ...filter, genre });
    setSelectedGenre(genre);
  };

  const onClickCountry = (country: string) => {
    setSelectedCountry(country);
  };

  const handleClickNextRecommendations = () => {
    const newIndex = indexList + 5;
    if (newIndex < recommendations.length) {
      setIndexList(newIndex);
      setCurrentRecommendations(recommendations.slice(indexList, newIndex));
    } else {
      setCurrentRecommendations(recommendations.slice(indexList));
      setIndexList(0);
    }
  };

  const onClickFiltrar = () => {
    setFilter({
      ...filter,
      indexes: selectedFeature,
      genre: selectedGenre,
      country: selectedCountry,
    });
    setShowFilter(false);
  };

  // Função para formatar a posição baseada no idioma
  // ✅ Isso retorna JSX com elementos controláveis
  const formatPosition = (position: number): JSX.Element => {
    const isEnglish = i18n.language === "en" || i18n.language === "en-US";

    if (isEnglish) {
      return (
        <span className="position-wrapper">
          <span className="hashtag">#</span>{" "}
          {/* Elemento separado = controle fino */}
          <span className="number">{position}</span>
        </span>
      );
    }

    return <span>{position}º</span>; // Português mantém formato original
  };

  const components = [
    ...currentRecommendations.map((recommendation, index) => (
      <div
        className="container-image"
        key={`RECOMMENDATION_${recommendation.artistIdSpotify}`}
      >
        <Badge
          count={
            <span
              className={`badge ${
                recommendation.position === 1 ? "first" : ""
              }`}
              style={{
                width: isDesktop ? 50 : 34,
                height: isDesktop ? 50 : 34,
              }}
            >
              {/* Número formatado */}
              {formatPosition(recommendation.position)}
            </span>
          }
        >
          <img
            src={randomImages[index]}
            alt="Profile"
            style={{
              width: isDesktop ? 70 : 50,
              height: isDesktop ? 70 : 50,
            }}
          />
        </Badge>
        <span className="label"> {recommendation.artistName}</span>
      </div>
    )),
    <div className="container-image" onClick={handleClickNextRecommendations}>
      <span
        className="ver-mais"
        style={{
          width: isDesktop ? 70 : 50,
          height: isDesktop ? 70 : 50,
        }}
      >
        +{recommendations.length - currentRecommendations.length}
      </span>

      <span className="label">{t("pages.Recomendacoes.verMais")}</span>
    </div>,
  ];

  useEffect(() => {
    setCurrentRecommendations(recommendations.slice(0, 5));
  }, [recommendations]);

  useEffect(() => {
    // if (!currentPlan) return;
    if (currentPlan === SubscriptionPlan.START) {
      // generate 5 random recommendations for START plan
      const generated = Array.from({ length: 5 }).map((_, i) => ({
        artistIdSpotify: `RAND_${Date.now()}_${i}_${Math.floor(
          Math.random() * 1e6
        )}`,
        artistIdStrm: null,
        artistName: `Artist ${Math.floor(Math.random() * 1000)}`,
        position: i + 1,
        roleTitle: null,
        similarityIndex: parseFloat((Math.random() * 0.5 + 0.5).toFixed(2)), // 0.50 - 1.00
      })) as Recommendation[];

      setRecommendations(generated);
    } else {
      getRecommendations();
    }
  }, [getRecommendations, currentPlan]);

  if (etapaAtual === PageStep.Inicio)
    return (
      <div className={`recomendacao-collab ${isDesktop && "web"}`}>
        <div className="page-container">
          <div className="inicio splash">
            <div className="background" />
            <div className="content">
              <Avatar src={userArtist?.profileImage} size={100} />
              <h1 className="title"> {t("pages.Recomendacoes.title")}</h1>
              <p className="text">{t("pages.Recomendacoes.intro")}</p>
              <button className="botao-branco" onClick={handleNextStep}>
                {t("pages.Recomendacoes.inicio")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className={`recomendacao-collab ${isDesktop && "web"}`}>
      <Spin
        spinning={loading}
        indicator={
          <LoadingOutlined
            style={{
              fontSize: 72,
            }}
            spin={false}
          />
        }
      >
        <div className={`page-container ${isDesktop && "web"}`}>
          <div className="inicio">
            <div>
              {currentPlan === SubscriptionPlan.START && (
                <div
                  className="block-upgrade"
                  style={{
                    width: isMobile ? fullScreenWidth - 100 : 500,
                    height: isMobile ? fullScreenWidth - 100 : 500,
                  }}
                >
                  <SubscriptionDefaultCTA
                    title={
                      <Trans
                        i18nKey={t(
                          "components.Subscription.upgradeFeatureCTATitle"
                        )}
                        components={{
                          span: <span className="cor-destaque" />,
                        }}
                      />
                    }
                  />
                </div>
              )}
              <div
                style={{
                  filter:
                    currentPlan === SubscriptionPlan.START
                      ? "blur(8px)"
                      : "none",
                }}
              >
                <ClusterChart
                  mainImage={userArtist?.profileImage}
                  components={components}
                  width={isMobile ? fullScreenWidth - 100 : 500}
                />
              </div>
              <div
                className={`filtros ${isMobile ? "mobile" : "web"}`}
                style={{ width: isMobile ? "calc(100% - 24px)" : 500 }}
              >
                {showFilter ? (
                  <div className="caracteristicas-container">
                    <div className="header">
                      <span
                        className="clickable"
                        onClick={() => setShowFilter(false)}
                      >
                        {t("pages.Recomendacoes.cancelar")}
                      </span>
                      <span className="title">
                        {t("pages.Recomendacoes.filtros")}
                      </span>
                      {currentPlan !== SubscriptionPlan.START ? (
                        <span className="clickable" onClick={onClickFiltrar}>
                          {t("pages.Recomendacoes.filtrar")}
                        </span>
                      ) : (
                        <span></span>
                      )}
                    </div>
                    <span className="title">
                      {t("pages.Recomendacoes.caracteristicas")}
                    </span>
                    <div style={{ display: "flex" }}>
                      {isDesktop && showLeftArrow && (
                        <div
                          className="round-arrow-button"
                          style={{
                            position: "absolute",
                            zIndex: 99,
                            marginTop: 8,
                          }}
                        >
                          <LeftOutlined
                            onClick={() => handleScrollLeft(indexRef.current)}
                          />
                        </div>
                      )}
                      <div className="scroll-container" ref={indexRef}>
                        <div className="button-container">
                          <div
                            className={`caracteristica ${
                              selectedFeature === Caracteristica.Hitmaker
                                ? "selected"
                                : ""
                            }`}
                            onClick={() => {
                              onClickCaracteristica(Caracteristica.Hitmaker);
                            }}
                          >
                            <img
                              src={avatarHitmakerSrc}
                              alt="Avatar"
                              className="icon-button"
                            />
                            <div className="info">
                              <span className="name">
                                {t("pages.Recomendacoes.hitmaker")}
                              </span>
                              <span className="value">
                                {t("pages.Recomendacoes.hitmakerText")}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`caracteristica ${
                              selectedFeature === Caracteristica.Sniper
                                ? "selected"
                                : ""
                            }`}
                            onClick={() =>
                              onClickCaracteristica(Caracteristica.Sniper)
                            }
                          >
                            <img
                              src={avatarSniperSrc}
                              alt="Avatar"
                              className="icon-button"
                            />
                            <div className="info">
                              <span className="name">
                                {t("pages.Recomendacoes.sniper")}
                              </span>
                              <span className="value">
                                {t("pages.Recomendacoes.sniperText")}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`caracteristica ${
                              selectedFeature === Caracteristica.Massificado
                                ? "selected"
                                : ""
                            }`}
                            onClick={() =>
                              onClickCaracteristica(Caracteristica.Massificado)
                            }
                          >
                            <img
                              src={avatarMassificadoSrc}
                              alt="Avatar"
                              className="icon-button"
                            />
                            <div className="info">
                              <span className="name">
                                {t("pages.Recomendacoes.massificado")}
                              </span>
                              <span className="value">
                                {t("pages.Recomendacoes.massificadoText")}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`caracteristica ${
                              selectedFeature === Caracteristica.Ascendência
                                ? "selected"
                                : ""
                            }`}
                            onClick={() =>
                              onClickCaracteristica(Caracteristica.Ascendência)
                            }
                          >
                            <img
                              src={avatarAscendenciaSrc}
                              alt="Avatar"
                              className="icon-button"
                            />
                            <div className="info">
                              <span className="name">
                                {t("pages.Recomendacoes.tendencia")}
                              </span>
                              <span className="value">
                                {t("pages.Recomendacoes.tendenciaText")}
                              </span>
                            </div>
                          </div>
                          <div
                            className={`caracteristica ${
                              selectedFeature === Caracteristica.Atemporal
                                ? "selected"
                                : ""
                            }`}
                            onClick={() =>
                              onClickCaracteristica(Caracteristica.Atemporal)
                            }
                          >
                            <img
                              src={avatarAtemporalSrc}
                              alt="Avatar"
                              className="icon-button"
                            />
                            <div className="info">
                              <span className="name">
                                {t("pages.Recomendacoes.atemporal")}
                              </span>
                              <span className="value">
                                {t("pages.Recomendacoes.atemporalText")}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      {isDesktop && showRightArrow && (
                        <div
                          className="round-arrow-button"
                          style={{
                            position: "absolute",
                            right: 12,
                            marginTop: 8,
                          }}
                        >
                          <RightOutlined
                            onClick={() => handleScrollRight(indexRef.current)}
                          />
                        </div>
                      )}
                    </div>
                    <div className="genero-container">
                      <span className="title">
                        {t("pages.Recomendacoes.pais")}
                      </span>
                      <Select
                        value={selectedCountry}
                        options={countryOptions.map((country: any) => ({
                          label: country.label,
                          value: country.value,
                        }))}
                        style={{ width: "100%" }}
                        prefix={<SearchIcon className="search-icon" />}
                        suffixIcon={null}
                        showSearch
                        filterOption={filterOptionDiacritics}
                        allowClear
                        onChange={(value: string) => setSelectedCountry(value)}
                        placeholder={t("pages.Recomendacoes.paisPlaceholder")}
                      />
                      <div style={{ display: "flex", marginTop: 12 }}>
                        {countryFiltersOptions.map(
                          (country: any, index: number) => (
                            <Tag
                              key={`COUNTRY_${index}_${country.value}`}
                              className={`
                            tag-genre ${
                              selectedCountry === country.value
                                ? "selected"
                                : "not-selected"
                            }`}
                              onClick={() => onClickCountry(country.value)}
                            >
                              {country.label}
                            </Tag>
                          )
                        )}
                        <Tag
                          className="tag-genre"
                          style={{ cursor: "default" }}
                        >
                          +{countryOptions.length - countryFilters.length}
                        </Tag>
                      </div>
                    </div>
                    {filter.role === Role.Feat && userArtist?.stage >= 7 && (
                      <div className="genero-container">
                        <span className="title">
                          {t("pages.Recomendacoes.genero")}
                        </span>
                        <Select
                          value={selectedGenre}
                          options={filteredOtherGenres.map((genre: any) => ({
                            label: genre.label,
                            value: genre.value,
                          }))}
                          style={{ width: "100%" }}
                          prefix={<SearchIcon className="search-icon" />}
                          suffixIcon={null}
                          showSearch
                          filterOption={filterOptionDiacritics}
                          onChange={(value: string) => setSelectedGenre(value)}
                          placeholder={t(
                            "pages.Recomendacoes.generoPlaceholder"
                          )}
                          allowClear
                        />
                        <div style={{ display: "flex", marginTop: 12 }}>
                          {genreFiltersOptions.map(
                            (genre: any, index: number) => (
                              <Tag
                                key={`GENRE_${index}_${genre.value}`}
                                className={`
                            tag-genre ${
                              selectedGenre === genre.value
                                ? "selected"
                                : "not-selected"
                            }`}
                                onClick={() => onClickGenre(genre.value)}
                              >
                                {genre.label}
                              </Tag>
                            )
                          )}
                          <Tag
                            className="tag-genre"
                            style={{ cursor: "default" }}
                          >
                            +{genreOptions.length - genreFilters.length}
                          </Tag>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="scroll-container">
                      <div className="button-container">
                        <button
                          className={`scroll-button botao-roxo ${
                            filter.role === Role.Produtor ? "selected" : ""
                          }`}
                          onClick={() => onClickRole(Role.Produtor)}
                        >
                          <img
                            src={avatarProdutoresSrc}
                            alt="Avatar"
                            className="icon-button"
                          />
                          {t("pages.Recomendacoes.produtores")}
                        </button>
                        <button
                          className={`scroll-button botao-roxo ${
                            filter.role === Role.Compositor ? "selected" : ""
                          }`}
                          onClick={() => onClickRole(Role.Compositor)}
                        >
                          <img
                            src={avatarCompositoresSrc}
                            alt="Avatar"
                            className="icon-button"
                          />
                          {t("pages.Recomendacoes.compositores")}
                        </button>
                        <button
                          className={`scroll-button botao-roxo ${
                            filter.role === Role.Feat ? "selected" : ""
                          }`}
                          onClick={() => onClickRole(Role.Feat)}
                        >
                          <img
                            src={avatarFeatSrc}
                            alt="Avatar"
                            className="icon-button"
                          />
                          {t("pages.Recomendacoes.feat")}
                        </button>
                      </div>
                    </div>
                    <div
                      className="caracteristicas-container"
                      onClick={() => setShowFilter(true)}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                      >
                        <span>{t("pages.Recomendacoes.filtros")}</span>
                        <EllipsisIcon />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default RecomendacaoCollab;
